import type {
  RecentJournalFeedEntry,
  RecentProductJournalFeedEntry,
} from "../../api/WarehouseApi";
import { isRestoreComment } from "../../domain/journal";
import { Action } from "../../types/entities";
import type { RealtimeFeedItem } from "../RealtimeStore";
import {
  isBoxEntityJournalEntry,
  isRecentProductJournalEntry,
  resolveBoxNameForNotification,
  resolveEntityNameForNotification,
} from "./scheduler";

export interface NotificationPayload {
  title: string;
  body: string;
  tag: string;
}

export function buildExpiringNotificationPayload(
  event: RealtimeFeedItem,
): NotificationPayload {
  return {
    title: "Срок годности истекает",
    body: event.description || "Проверьте товары с истекающим сроком",
    tag: `expiring:${event.productId || event.id}`,
  };
}

export function buildRealtimeMutationNotificationPayload(
  event: RealtimeFeedItem,
): NotificationPayload {
  return {
    title: event.title || "Событие склада",
    body: event.description || "Данные склада обновлены",
    tag: `entity:${event.id}`,
  };
}

function buildProductJournalNotificationPayload(
  entry: RecentProductJournalFeedEntry,
): NotificationPayload {
  const boxEntity = isBoxEntityJournalEntry(entry);
  const entityName = resolveEntityNameForNotification(entry);
  const entityLabel = boxEntity ? "Коробка" : "Товар";
  const boxName = resolveBoxNameForNotification(entry);

  let title = boxEntity ? "Операция с коробкой" : "Операция с товаром";
  if (entry.action === Action.take) {
    title = boxEntity
      ? "Коробка изъята из родительской коробки"
      : "Товар изъят из коробки";
  } else if (entry.action === Action.destroy) {
    title = boxEntity ? "Коробка списана" : "Товар списан";
  } else if (entry.action === Action.put && isRestoreComment(entry.comment)) {
    title = boxEntity ? "Коробка восстановлена" : "Товар восстановлен";
  } else if (entry.action === Action.put) {
    title = boxEntity
      ? "Коробка помещена в родительскую коробку"
      : "Товар добавлен в коробку";
  }

  const comment = (entry.comment || "").trim();
  const body = comment
    ? `${entityLabel}: ${entityName}\nКоробка: ${boxName}\nКомментарий: ${comment}`
    : `${entityLabel}: ${entityName}\nКоробка: ${boxName}`;

  return {
    title,
    body,
    tag: `journal:${entry.id}`,
  };
}

export function buildJournalNotificationPayload(
  entry: RecentJournalFeedEntry,
): NotificationPayload {
  if (!isRecentProductJournalEntry(entry)) {
    const boxName = (entry.boxName || "").trim() || entry.boxId;
    return {
      title: "Коробка восстановлена",
      body: `Коробка: ${boxName}`,
      tag: `journal:${entry.id}`,
    };
  }

  return buildProductJournalNotificationPayload(entry);
}

export function buildSystemTestNotificationPayload(
  delivered: number,
): NotificationPayload {
  return {
    title: "Тестовый push",
    body: `Тестовое уведомление отправлено. Доставлено: ${delivered}.`,
    tag: `webpush:test:${Date.now()}`,
  };
}

export function buildLocalNotificationOptions(
  body: string,
  tag: string,
): NotificationOptions {
  return {
    body,
    tag,
    requireInteraction: true,
    icon: "/icons/icon192_rounded.png",
    badge: "/icons/icon192_rounded.png",
    data: {
      url: "/",
    },
  };
}

export function buildDefaultNotificationOptions(
  body: string,
  tag: string,
): NotificationOptions {
  return {
    body,
    tag,
    data: {
      url: "/",
    },
  };
}
